import type {
  EquipmentRead,
  SwitchingDetailed,
  SwitchingPremiseRead,
  SwitchingPremiseViolationStatic,
} from '@gridcon-energy/schaltungstool-backend'
import {
  SwitchingBlockRestrictionTypeEnum,
  SwitchingCategoryTypeEnum,
  SwitchingPremiseViolationStaticEnum,
} from '@gridcon-energy/schaltungstool-backend'
import dayjs from 'dayjs/esm'

export const SNAPPING_RESOLUTION: dayjs.QUnitType = 'day'
export enum ResolutionModeEnum {
  AUTO = 'auto', // use the breakpoints
  MANUAL = 'manual', // use user defined values
}

/**
 * Used to handle end dates of switchings as 23:59:59
 * See: https://git.hw.ag/gridcon-energy/schaltungstool-frontend/-/issues/150
 */
export const asEndOfDay = (date: dayjs.ConfigType): dayjs.Dayjs => {
  return dayjs(date).endOf('day')
}

export enum StartEndPresetEnum {
  EARLIEST_LATEST_SWITCHING_DATES = 'earliest_latest_switching_dates',
  ACTIVITY_DATA_FILTER_START_END = 'activity_data_filter_start_end',
  START_AND_INTERVALS = 'start_and_intervals',
}
export enum ResolutionEnum {
  day = 'day',
  week = 'week',
  month = 'month',
  quarter = 'quarter',
  year = 'year',
  twoYears = 'twoYears',
}
export type SeekOptions = '<' | '<<' | '>>' | '>'
export enum GroupingPresetEnum {
  Without = 'Without',
  Equipment = 'Equipment',
  EquipmentProject = 'EquipmentProject',
  EquipmentProjectSubProject = 'EquipmentProjectSubProject',
}

export type RowLabelConfiguration = {
  SubProject: number
  Project: number
  Equipment: number
}

export enum BarTypesEnum {
  VIOLATION_PREVIOUS = 'violation_previous',
  VIOLATION_PARALLEL = 'violation_parallel',
  BACKGROUND = 'background',
  OVERLAP = 'overlap',
  MAIN = 'main',
  INITIAL_PERIOD = 'initial_period',
  OFFLINE_RANGE = 'offline_range',
  LIMIT_VIOLATION = 'limit_violation',
}

export type GanttRowBar<T extends BarTypesEnum = BarTypesEnum> = {
  start: string
  end: string
  type: T
}
export type GanttRowBarSwitching = GanttRowBar<BarTypesEnum.MAIN> & {
  switching: SwitchingDetailed
  relatedSwitchingBlocks: number[]
}

export const SimpleOfflineAssignable = new Set([
  SwitchingCategoryTypeEnum.Disabled,
  SwitchingCategoryTypeEnum.Protected,
  SwitchingCategoryTypeEnum.BypassMode,
])

/**
 * Represents the parsed data of backend "row_mapping" (result of sql-grouped by)
 */
export type GanttColumnData = {
  column: string
  table: string
  value: unknown // usually string|number|null (cell value in sql db)
}

export type GanttRowData = {
  rowId: string // used for highlight/select, expanded state, dividers
  // columns: { [Table: string]: { [Col: string]: string } }
  columns: GanttColumnData[]

  // offline states for all equipments in row
  // TODO: transform to offline bar type GanttRowBar_NEW<SecondaryBarsEnum.OFFLINE_RANGE>
  // CAUTION: no refactor intellisense for following - filter uses string dot notation
  bars: GanttRowBarSwitching[]
}

/**
 * Switching Premise Violations Dynamic - dynamische Schaltungsprämissen
 */
export enum PremiseViolationDynamicEnum {
  OFFLINE = 'offline',
  PROTECTIVE = 'protective',
  ONLINE = 'online',
}
// CSS custom properties
export const PremiseViolationDynamicCSSVariables: {
  [key in PremiseViolationDynamicEnum]: `--premise-violation-dynamic-${key}`
} = {
  [PremiseViolationDynamicEnum.OFFLINE]: '--premise-violation-dynamic-offline',
  [PremiseViolationDynamicEnum.PROTECTIVE]: '--premise-violation-dynamic-protective',
  [PremiseViolationDynamicEnum.ONLINE]: '--premise-violation-dynamic-online',
} as const

// CSS Classes
export const PremiseViolationDynamicCSSClasses: {
  [key in PremiseViolationDynamicEnum]: `premise-violation-dynamic-${key}`
} = {
  [PremiseViolationDynamicEnum.OFFLINE]: 'premise-violation-dynamic-offline',
  [PremiseViolationDynamicEnum.ONLINE]: 'premise-violation-dynamic-online',
  [PremiseViolationDynamicEnum.PROTECTIVE]: 'premise-violation-dynamic-protective',
}
export type PremiseViolationCSSClassesEnum =
  (typeof PremiseViolationDynamicCSSClasses)[keyof typeof PremiseViolationDynamicCSSClasses]

/**
 * Switching Premise Violations Static - statische Schaltungsprämissen
 */

// CSS custom properties
export const PremiseViolationStaticCSSVariables: {
  [key in SwitchingPremiseViolationStaticEnum]: `--premise-violation-static-${key}`
} = {
  [SwitchingPremiseViolationStaticEnum.ConsecutiveActivated]: '--premise-violation-static-consecutive_activated',
  [SwitchingPremiseViolationStaticEnum.ConsecutiveDeactivated]: '--premise-violation-static-consecutive_deactivated',
} as const

// CSS Classes
export const PremiseViolationStaticCSSClasses: {
  [key in SwitchingPremiseViolationStaticEnum]: `premise-violation-static-${key}`
} = {
  [SwitchingPremiseViolationStaticEnum.ConsecutiveActivated]: 'premise-violation-static-consecutive_activated',
  [SwitchingPremiseViolationStaticEnum.ConsecutiveDeactivated]: 'premise-violation-static-consecutive_deactivated',
}
export type PremiseViolationStaticCSSClassesEnum =
  (typeof PremiseViolationStaticCSSClasses)[keyof typeof PremiseViolationStaticCSSClasses]

/**
 * Switching Block Restrictions - Schaltungsblockprämissen
 */
// CSS custom properties
const SBRestrictionEnum = SwitchingBlockRestrictionTypeEnum
export const SBRestrictionCSSVariables: {
  [key in SwitchingBlockRestrictionTypeEnum]: `--sb-restriction-${key}`
} = {
  [SBRestrictionEnum.ParallelForbidden]: '--sb-restriction-parallel_forbidden',
  [SBRestrictionEnum.Previous]: '--sb-restriction-previous',
  [SBRestrictionEnum.Parallel]: '--sb-restriction-parallel',
} as const

// CSS Classes
export const SBRestrictionCSSClasses: {
  [key in SwitchingBlockRestrictionTypeEnum]: `sb-restriction-${key}`
} = {
  [SBRestrictionEnum.ParallelForbidden]: 'sb-restriction-parallel_forbidden',
  [SBRestrictionEnum.Previous]: 'sb-restriction-previous',
  [SBRestrictionEnum.Parallel]: 'sb-restriction-parallel',
}
export type SBRestrictionCSSClassesEnum = (typeof SBRestrictionCSSClasses)[keyof typeof SBRestrictionCSSClasses]

/**
 * TBT Daily
 */
export const TbtDailyCSSVariables = {
  color: '--switching-info-tbt-daily-color',
  spacing: '--switching-info-tbt-daily-spacing',
  angle: '--switching-info-tbt-daily-angle',
}

/**
 * Other
 */
// CSS custom properties
export const OtherCSSVariables = {
  gridLineColor: '--grid-line-color',
  rowExpandedBackground: '--row-expanded-background',

  barMainHeight: '--bar-main-height',
  barMainOpacity: '--bar-main-opacity',

  barBgHeight: '--bar-bg-height',
  barBgOpacity: '--bar-bg-opacity',
  barBgColorOverlap: '--bar-bg-color-overlap',

  barOverlapOpacity: '--bar-overlap-opacity',

  barTimeFrameOriginalColor: '--bar-time-frame-original-color',
  barTimeFrameWidth: '--bar-time-frame-width',

  barLimitViolationColor: '--bar-limit-violation-color',
} as const

export const GanttCSSVariables = {
  ...PremiseViolationDynamicCSSVariables,
  ...SBRestrictionCSSVariables,
  ...OtherCSSVariables,
}

export type DayjsRange = {
  start: dayjs.Dayjs
  end: dayjs.Dayjs
}

export interface ViolatedSwitchingPremiseDynamic {
  premise: SwitchingPremiseRead
  equipments: EquipmentRead[]
  offline: DayjsRange[]
  online: DayjsRange[]
  protective: DayjsRange[]
}

export interface ViolatedSwitchingPremiseStatic extends SwitchingPremiseViolationStatic {
  startEndRange: DayjsRange
}

export interface SwitchingBlockPreviousViolation {
  currentSwitchingBlockId: number
  currentBarId: number
  currentBarStart: dayjs.Dayjs
  previousSwitchingBlockId: number
  previousBarId: number
  previousBarEnd: dayjs.Dayjs
}

export interface SwitchingBlockForbiddenViolation {
  switchingBlockId?: number
  barId: number
  nonParallelSwitchingBlockId?: number
  nonParallelBarIds?: number[]
  start: dayjs.Dayjs
  end: dayjs.Dayjs
}

export interface SwitchingDiagrammRowLabelDividers {
  equipments: Set<string>
  projects: Set<string>
  subProjects: Set<string>
}

export enum StartEndEnum {
  start = 'start',
  end = 'end',
}

/**
 * Key: source SwitchingBlock
 * Value: related SwitchingBlocks
 */
export type SwitchingBlockRestrictionRelations = { [key: number]: number[] }

export type SwitchingTimeFinalizeReset = 'finalize' | 'reset'
export type DateRangeNullable = {
  start: string | null
  end: string | null
}
export type DateRangeRequired = {
  start: string
  end: string
}

export interface SwitchingBlockMinMaxRange {
  switchingBlockId: number
  dateRange: DateRangeRequired
}

export type SwitchingBlocksMinMaxRange = SwitchingBlockMinMaxRange[]

/**
 * Types for typesafe style generation - Take care changing structure :)
 */
type CSSPropRestrictions = (typeof SBRestrictionCSSVariables)[keyof typeof SBRestrictionCSSVariables]
type CSSPropSwitchingPremiseDynamic =
  (typeof PremiseViolationDynamicCSSVariables)[keyof typeof PremiseViolationDynamicCSSVariables]
type CSSPropSwitchingPremiseStatic =
  (typeof PremiseViolationStaticCSSVariables)[keyof typeof PremiseViolationStaticCSSVariables]
type CSSPropOther = (typeof OtherCSSVariables)[keyof typeof OtherCSSVariables]
type CSSPropTbtDaily = (typeof TbtDailyCSSVariables)[keyof typeof TbtDailyCSSVariables]
export type GanttCSSProperties = {
  [key in
    | CSSPropRestrictions
    | CSSPropSwitchingPremiseDynamic
    | CSSPropSwitchingPremiseStatic
    | CSSPropOther
    | CSSPropTbtDaily]: string
}
